import { esignApi, kycApi, subscriptionsApi } from '../services/api'
import { User } from '../types/auth'

type AuthPayload = {
  user?: User | null
  has_active_plan?: boolean
}

export const resolvePostAuthRoute = ({ user }: AuthPayload): string => {
  if (!user) return '/login'
  if (user.is_admin) return '/admin'
  return '/dashboard'
}

export const evaluateRouteAccess = async (
  user: User | null,
  options: { requirePlan?: boolean } = {},
): Promise<{ allowed: boolean; redirectTo?: string }> => {
  if (!user) return { allowed: false, redirectTo: '/login' }
  if (user.is_admin) return { allowed: true }

  if (options.requirePlan === false) return { allowed: true }

  try {
    const subscription = await subscriptionsApi.getCurrentSubscription()
    if (subscription.status === 'active' && subscription.days_remaining >= 0) {
      return { allowed: true }
    }
  } catch {
    // The existing endpoint returns an error when there is no current plan.
  }

  return { allowed: false, redirectTo: '/dashboard/subscription' }
}

function withNext(path: string, next?: string): string {
  if (!next) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}next=${encodeURIComponent(next)}`
}

export const evaluatePurchaseAccess = async (
  user: User | null,
  options: { next?: string } = {},
): Promise<{ allowed: boolean; redirectTo?: string }> => {
  if (!user) return { allowed: false, redirectTo: '/login' }

  const [kycResult, esignResult] = await Promise.allSettled([
    kycApi.getKYCStatus(),
    esignApi.getStatus(),
  ])

  const kyc = kycResult.status === 'fulfilled' ? kycResult.value : null
  const kycComplete = Boolean(kyc && kyc.status !== 'not_started')
  if (!kycComplete) {
    return { allowed: false, redirectTo: withNext('/dashboard/kyc', options.next) }
  }

  const esign = esignResult.status === 'fulfilled' ? esignResult.value : null
  const esignStatus = esign?.esign_status || (kyc as any)?.esign_status
  const esignComplete = esignStatus === 'completed'
  if (!esignComplete) {
    return { allowed: false, redirectTo: withNext('/dashboard/esign', options.next) }
  }

  return { allowed: true }
}
